import { Injectable, OnDestroy } from '@angular/core';

/**
 * Service responsible for managing fullscreen modes (full-window and native browser fullscreen).
 * Extracted from VideoPlayerComponent to reduce component complexity.
 *
 * Manages:
 * - Full-window mode (CSS-based windowed fullscreen)
 * - Native browser fullscreen (Fullscreen API with vendor prefixes)
 * - Body class management for fullscreen styling
 * - Scroll-to-refresh prevention in fullscreen
 * - Wheel event listener for fullscreen navigation
 */
@Injectable({
  providedIn: 'root',
})
export class FullscreenService implements OnDestroy {
  isFullWindowMode = false;
  isNativeFullscreen = false;

  // Scroll-to-refresh prevention
  private scrollToRefreshTouchStartY = 0;
  private scrollToRefreshTouchStartHandler = (e: TouchEvent) => {
    this.scrollToRefreshTouchStartY = e.touches[0].clientY;
  };
  private scrollToRefreshTouchMoveHandler = (e: TouchEvent) => {
    const touchY = e.touches[0].clientY;
    const touchDiff = touchY - this.scrollToRefreshTouchStartY;

    // If at top of page and trying to scroll down (pull to refresh)
    if (window.scrollY === 0 && touchDiff > 0) {
      e.preventDefault();
    }
  };

  // Wheel listener for navigation in fullscreen
  private fullscreenWheelListener: ((event: WheelEvent) => void) | null = null;

  // Bound handler reference for proper cleanup
  private boundHandleFullscreenChange = this.handleFullscreenChange.bind(this);

  // Callback for fullscreen change events
  private onFullscreenChangeCallback: ((isFullscreen: boolean) => void) | null = null;

  // Callback for wheel navigation in fullscreen
  private onWheelNavigateCallback: ((direction: 'next' | 'previous') => void) | null = null;

  /**
   * Register a callback for fullscreen state changes.
   * The component uses this to react to native fullscreen changes.
   */
  setOnFullscreenChangeCallback(callback: (isFullscreen: boolean) => void): void {
    this.onFullscreenChangeCallback = callback;
  }

  /**
   * Register a callback for wheel navigation in fullscreen.
   */
  setOnWheelNavigateCallback(callback: (direction: 'next' | 'previous') => void): void {
    this.onWheelNavigateCallback = callback;
  }

  /**
   * Start listening for native fullscreen change events.
   * Call this during component initialization.
   */
  addFullscreenEventListeners(): void {
    document.addEventListener('fullscreenchange', this.boundHandleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.boundHandleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.boundHandleFullscreenChange);
    document.addEventListener('msfullscreenchange', this.boundHandleFullscreenChange);
  }

  /**
   * Stop listening for native fullscreen change events.
   * Call this during component cleanup.
   */
  removeFullscreenEventListeners(): void {
    document.removeEventListener('fullscreenchange', this.boundHandleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.boundHandleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.boundHandleFullscreenChange);
    document.removeEventListener('msfullscreenchange', this.boundHandleFullscreenChange);
  }

  /**
   * Toggle full window mode (CSS-based windowed fullscreen)
   */
  toggleFullWindowMode(): void {
    // If native fullscreen is active, exit it first
    if (this.isNativeFullscreen) {
      this.exitNativeFullscreen();
    }

    this.isFullWindowMode = !this.isFullWindowMode;
    console.log(
      `Full window mode: ${this.isFullWindowMode ? 'enabled' : 'disabled'}`
    );

    // Add/remove class to body for styling
    if (this.isFullWindowMode) {
      document.body.classList.add('full-window-mode');
      document.body.classList.add('full-window-active');
      this.addScrollToRefreshPrevention();
    } else {
      document.body.classList.remove('full-window-mode');
      document.body.classList.remove('full-window-active');
      this.removeScrollToRefreshPrevention();
    }
  }

  /**
   * Exit full window mode
   */
  exitFullWindow(): void {
    if (this.isFullWindowMode) {
      this.isFullWindowMode = false;
      document.body.classList.remove('full-window-mode');
      this.removeScrollToRefreshPrevention();
      console.log('Exited full window mode');
    }
  }

  /**
   * Toggle native fullscreen mode
   */
  toggleNativeFullscreen(): void {
    // If full window mode is active, exit it first
    if (this.isFullWindowMode) {
      this.exitFullWindow();
    }

    if (!this.isNativeFullscreen) {
      this.enterNativeFullscreen();
    } else {
      this.exitNativeFullscreen();
    }
  }

  /**
   * Enter native fullscreen mode
   */
  enterNativeFullscreen(): void {
    // Try to put the video player container into fullscreen instead of the entire document
    const container = document.querySelector('.video-player-container') as HTMLElement & {
      requestFullscreen?: () => Promise<void>;
      webkitRequestFullscreen?: () => Promise<void>;
      msRequestFullscreen?: () => Promise<void>;
    };

    if (container) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else {
      // Fallback to document element if container not found
      const element = document.documentElement as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void>;
        msRequestFullscreen?: () => Promise<void>;
      };

      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    }
  }

  /**
   * Exit native fullscreen mode
   */
  exitNativeFullscreen(): void {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void>;
      msExitFullscreen?: () => Promise<void>;
    };

    if (doc.exitFullscreen) {
      doc.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen();
    }
  }

  /**
   * Add wheel event listener for navigation in fullscreen.
   * @param videoElement The video element to attach the listener to
   */
  addFullscreenWheelListener(videoElement: HTMLVideoElement): void {
    if (!this.isNativeFullscreen) return;

    this.fullscreenWheelListener = (event: WheelEvent) => {
      event.preventDefault();
      if (event.deltaY > 0) {
        this.onWheelNavigateCallback?.('next');
      } else if (event.deltaY < 0) {
        this.onWheelNavigateCallback?.('previous');
      }
    };

    videoElement.addEventListener('wheel', this.fullscreenWheelListener, { passive: false });
  }

  /**
   * Remove wheel event listener.
   * @param videoElement The video element to remove the listener from
   */
  removeFullscreenWheelListener(videoElement: HTMLVideoElement | null): void {
    if (videoElement && this.fullscreenWheelListener) {
      videoElement.removeEventListener('wheel', this.fullscreenWheelListener);
    }
    this.fullscreenWheelListener = null;
  }

  /**
   * Clean up all fullscreen body classes. Call during component destroy.
   */
  cleanupBodyClasses(): void {
    if (this.isFullWindowMode) {
      document.body.classList.remove('full-window-mode');
      document.body.classList.remove('full-window-active');
    }
    if (this.isNativeFullscreen) {
      document.body.classList.remove('native-fullscreen-mode');
      document.body.classList.remove('full-window-active');
    }
    this.removeScrollToRefreshPrevention();
  }

  /**
   * Handle close button touch for mobile devices.
   */
  onCloseButtonTouch(event: Event): void {
    // Prevent the event from bubbling up to avoid conflicts with swipe gestures
    event.stopPropagation();
    event.preventDefault();

    // Exit fullscreen mode - check which mode we're in and exit accordingly
    if (this.isFullWindowMode) {
      this.exitFullWindow();
    } else {
      this.exitNativeFullscreen();
    }

    console.log('Close button touched - exiting fullscreen');
  }

  /**
   * Enter full-window mode directly (used when opening a new video in fullscreen)
   */
  enterFullWindowMode(): void {
    this.isFullWindowMode = true;
    document.body.classList.add('full-window-mode');
    document.body.classList.add('full-window-active');
  }

  ngOnDestroy(): void {
    this.cleanupBodyClasses();
    this.removeFullscreenEventListeners();
  }

  /**
   * Add scroll-to-refresh prevention listeners
   */
  private addScrollToRefreshPrevention(): void {
    document.addEventListener('touchstart', this.scrollToRefreshTouchStartHandler, { passive: false });
    document.addEventListener('touchmove', this.scrollToRefreshTouchMoveHandler, { passive: false });
    // Also apply overscroll behavior
    document.body.style.overscrollBehavior = 'none';
    console.log('Scroll-to-refresh prevention enabled');
  }

  /**
   * Remove scroll-to-refresh prevention listeners
   */
  private removeScrollToRefreshPrevention(): void {
    document.removeEventListener('touchstart', this.scrollToRefreshTouchStartHandler);
    document.removeEventListener('touchmove', this.scrollToRefreshTouchMoveHandler);
    // Restore overscroll behavior
    document.body.style.overscrollBehavior = '';
    console.log('Scroll-to-refresh prevention disabled');
  }

  /**
   * Handle fullscreen change events (all vendor prefixes)
   */
  private handleFullscreenChange(): void {
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      mozFullScreenElement?: Element;
      msFullscreenElement?: Element;
    };

    const isFullscreen = !!(document.fullscreenElement ||
                            doc.webkitFullscreenElement ||
                            doc.mozFullScreenElement ||
                            doc.msFullscreenElement);

    this.isNativeFullscreen = isFullscreen;
    console.log(`Native fullscreen: ${this.isNativeFullscreen ? 'enabled' : 'disabled'}`);

    // Add/remove body class for native fullscreen styling
    if (this.isNativeFullscreen) {
      document.body.classList.add('native-fullscreen-mode');
      this.addScrollToRefreshPrevention();
    } else {
      document.body.classList.remove('native-fullscreen-mode');
      this.removeScrollToRefreshPrevention();
    }

    // Notify the component
    this.onFullscreenChangeCallback?.(this.isNativeFullscreen);
  }
}
